import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@shared/context/useAuth';
import { taskRequestApi } from '../services/taskRequestApi';
import { useApiMutation } from '@hooks/useApiMutation';
import type { UserSearchResult } from '../types/taskRequest';

interface AssignUserDialogProps {
  taskId: number;
  currentAssignee?: string | null;
  onAssigned: () => void;
  onCancel: () => void;
}

export default function AssignUserDialog({
  taskId,
  currentAssignee,
  onAssigned,
  onCancel,
}: Readonly<AssignUserDialogProps>) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assignMutation = useApiMutation(
    useCallback(async (assignData: { assignedToUserId: number | null; assignedToUserName: string | null }) => {
      await taskRequestApi.assignTaskRequest(taskId, assignData);
    }, [taskId]),
    { onSuccess: onAssigned, errorMessage: 'Failed to assign task. Please try again.' },
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await taskRequestApi.searchUsers(searchQuery.trim(), 10);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchQuery]);

  const handleAssign = async (targetUser: UserSearchResult) => {
    if (assignMutation.loading) return;
    await assignMutation.mutate({
      assignedToUserId: targetUser.userId,
      assignedToUserName: targetUser.displayName,
    });
  };

  const handleUnassign = async () => {
    if (assignMutation.loading) return;
    await assignMutation.mutate({
      assignedToUserId: null,
      assignedToUserName: null,
    });
  };

  return (
    <div className="task-dialog-overlay" onClick={onCancel}>
      <div className="task-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="task-dialog-header">
          <h3>Assign Task</h3>
        </div>
        <div className="task-dialog-body">
          {assignMutation.error && (
            <p className="task-dialog-error">
              {assignMutation.error}
            </p>
          )}

          {user && (
            <button
              type="button"
              className="task-assign-self-btn"
              onClick={() => handleAssign({ userId: user.userId, displayName: user.displayName, email: user.email })}
              disabled={assignMutation.loading}
            >
              Assign to Myself ({user.displayName})
            </button>
          )}

          <div className="task-dialog-field">
            <label htmlFor="assign-user-search">Search Users</label>
            <input
              id="assign-user-search"
              type="text"
              placeholder="Type a name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div className="task-user-search-results">
            {currentAssignee && (
              <div className="task-user-unassign" onClick={handleUnassign}>
                Unassign (currently: {currentAssignee})
              </div>
            )}

            {isSearching ? (
              <div className="task-user-search-empty">Searching...</div>
            ) : searchQuery.trim() && results.length === 0 ? (
              <div className="task-user-search-empty">No users found</div>
            ) : (
              results.map((user) => (
                <div
                  key={user.userId}
                  className="task-user-search-item"
                  onClick={() => handleAssign(user)}
                >
                  <span className="task-user-search-name">{user.displayName}</span>
                  <span className="task-user-search-email">{user.email}</span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="task-dialog-footer">
          <button
            type="button"
            className="task-action-btn task-action-btn-secondary"
            onClick={onCancel}
            disabled={assignMutation.loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
